import React, {
    PropsWithChildren,
    useEffect,
    useState,
    useCallback,
    FormEvent,
    useContext,
    SetStateAction,
    Dispatch,
} from 'react';
import {
    ComboBox,
    IComboBox,
    IComboBoxOption,
    SelectableOptionMenuItemType,
} from '@fluentui/react';
import { getServicesList } from '../../AGSS/api/AGSSApi';
import { CancelToken } from 'axios';
import { usePromise } from '../hooks/usePromise';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import {
    getThemeFromString,
    setSelectedServiceNamesInLocalStorage,
} from '../util/localStorageUtils';
import { emitUserActionMetric } from '../util/metricsUtil';
import {
    ActionResult,
    FilterTypes,
    UserAction,
} from '../../Common/Enums/Metrics/MetricEnums';
import {
    componentComboBoxStyles,
    componentDivStyles,
} from '../util/CommonStyles';
import { SortSelectedOptions } from '../util/sortUtils';
import { comboBoxUtils } from '../util/comboBoxUtils';

interface IServiceNameDropDownProps extends PropsWithChildren<any> {
    selectedServices: string[];
    setSelectedServices?: Dispatch<SetStateAction<string[]>>;
    setPageNumber?: Dispatch<SetStateAction<number>>;
    isLoaded: boolean;
    isMobile?: boolean;
    placeholder?: string;
}

export const ServiceNameDropDown: React.FC<IServiceNameDropDownProps> = (
    props: IServiceNameDropDownProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const disabled = props.isLoaded ? false : true;
    const [options, setOptions] = useState<IComboBoxOption[]>([]);

    const getData = useCallback(async (cancelToken?: CancelToken) => {
        return await getServicesList(cancelToken);
    }, []);

    const [result, , dropdownLoaded] = usePromise(getData, true);

    const selectableOptions = options.filter(
        (option) =>
            (option.itemType === SelectableOptionMenuItemType.Normal ||
                option.itemType === undefined) &&
            !option.disabled
    );

    const sortedOptions = SortSelectedOptions(
        selectableOptions,
        props.selectedServices,
        'Service Names'
    );

    const onChange = (
        event: FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string
    ) => {
        // Prevent the selection from being cleared when the combobox loses focus.
        // With`allowFreeform`, Fluent UI's ComboBox commits any pending typed
        // value on blur and routes that commit through onChange. We only care
        // about user-initiated selection changes, so ignore blur-driven calls.
        if (event?.type === 'blur') {
            return;
        }

        comboBoxUtils(
            option,
            props.selectedServices,
            selectableOptions,
            props.setSelectedServices!,
            props.setPageNumber,
            setSelectedServiceNamesInLocalStorage,
            () =>
                emitUserActionMetric(
                    UserAction.DROPDOWN_FILTER,
                    FilterTypes.SERVICE_NAME,
                    ActionResult.SUCCESS,
                    process.env.REACT_APP_BUILD_VERSION
                )
        );
    };

    const divStyles = componentDivStyles(props.isMobile);

    const comboBoxStyles = componentComboBoxStyles(
        props.selectedServices.length,
        theme,
        props.isMobile
    );

    useEffect(() => {
        if (dropdownLoaded && result) {
            let comboboxOptions: IComboBoxOption[] = [
                {
                    key: 'selectAll',
                    text: 'Select All',
                    itemType: SelectableOptionMenuItemType.SelectAll,
                },
                {
                    key: 'Header1',
                    text: 'Service Names',
                    itemType: SelectableOptionMenuItemType.Header,
                },
            ];

            // Group IDs by name in a LOCAL Map (not component state),
            // so re-runs of this effect (re-fetches, React 18 StrictMode
            // double-invoke, etc.) can never accumulate duplicate IDs into
            // the key.
            const idsByName = new Map<string, string[]>();
            result.forEach((item) => {
                if (!item.name) {
                    return;
                }
                const existing = idsByName.get(item.name);
                if (existing) {
                    existing.push(item.id);
                } else {
                    idsByName.set(item.name, [item.id]);
                }
            });

            // Sort IDs before joining so the key is deterministic regardless
            // of the order the API returned the services in. Without this, a
            // saved selection like "abc,def" wouldn't match a freshly built
            // "def,abc" after a reload - which is exactly why selection
            // wasn't persisting.
            idsByName.forEach((ids, name) => {
                comboboxOptions.push({
                    key: [...ids].sort().join(','),
                    text: name,
                });
            });

            setOptions(comboboxOptions);
        }
    }, [result, dropdownLoaded]);

    return (
        <div style={divStyles}>
            {result && result.length > 0 && (
                <ComboBox
                    multiSelect
                    placeholder={props.placeholder || 'Service Name'}
                    ariaLabel={props.placeholder || 'Service Name'}
                    options={sortedOptions}
                    selectedKey={props.selectedServices}
                    onChange={onChange}
                    styles={comboBoxStyles}
                    disabled={disabled}
                    allowFreeform
                />
            )}
        </div>
    );
};
